import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Copy,
  MoreHorizontal,
  Calendar,
  Store,
  Package,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle,
  FileText,
  Activity,
  TrendingUp,
  Users,
  BarChart3,
  X
} from 'lucide-react';
import ApiService from '../services/api';

interface Merchant {
  id: number;
  name: string;
  logo?: string;
}

interface Offer {
  id: number;
  title: string;
  description?: string;
  merchant_id: number;
  merchant_name: string;
  city_id?: number;
  city_name?: string;
  branch_id?: number;
  branch_name?: string;
  status: 'active' | 'processing' | 'draft' | 'expired';
  start_date: string;
  end_date: string;
  products_count: number;
  views_count: number;
  created_at: string;
  updated_at: string;
}

const OffersManagement: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [merchantFilter, setMerchantFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [deletingOfferId, setDeletingOfferId] = useState<number | null>(null);

  // تحميل البيانات عند تحميل الصفحة
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 تحميل العروض والمتاجر...');
      
      // تحميل العروض والمتاجر والمدن بالتوازي
      const [offersResponse, merchantsResponse, citiesResponse] = await Promise.all([
        ApiService.getAllOffers(),
        ApiService.getMerchants(),
        ApiService.getCities()
      ]);

      console.log('📦 Offers loaded:', offersResponse);
      console.log('🏪 Merchants loaded:', merchantsResponse);
      console.log('🏙️ Cities loaded:', citiesResponse);

      setOffers(offersResponse || []);
      setMerchants(merchantsResponse?.merchants || merchantsResponse || []);
      setCities(citiesResponse || []);
      
      console.log('Debug - Merchants structure:', merchantsResponse);
      console.log('Debug - Cities structure:', citiesResponse);
      
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError('فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // إعادة تحميل البيانات
  const handleRefresh = () => {
    loadData();
  };

  // تعديل العرض
  const handleEditOffer = async (updatedData: any) => {
    try {
      console.log('Updating offer with data:', updatedData);
      await ApiService.updateOffer(editingOffer!.id, updatedData);
      setEditingOffer(null);
      loadData(); // إعادة تحميل البيانات
    } catch (error: any) {
      console.error('Error updating offer:', error);
      let errorMessage = 'فشل في تحديث العرض';
      
      // Try to extract more specific error information
      if (error.message) {
        try {
          // If the error message is JSON, parse it for detailed info
          const errorData = JSON.parse(error.message);
          if (errorData.detail) {
            if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map((err: any) => `${err.loc?.join('.')}: ${err.msg}`).join('\n');
            } else {
              errorMessage = errorData.detail;
            }
          }
        } catch {
          // If not JSON, use the message as is
          errorMessage = error.message;
        }
      }
      
      alert(`خطأ في تحديث العرض:\n${errorMessage}`);
    }
  };

  // حذف العرض
  const handleDeleteOffer = async (offerId: number) => {
    try {
      await ApiService.deleteOffer(offerId);
      setDeletingOfferId(null);
      setOffers(offers.filter(offer => offer.id !== offerId));
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('فشل في حذف العرض');
    }
  };

  // فلترة العروض
  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.merchant_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || offer.status === statusFilter;
    const matchesMerchant = merchantFilter === 'all' || offer.merchant_id.toString() === merchantFilter;
    
    return matchesSearch && matchesStatus && matchesMerchant;
  });

  // التصفح بالصفحات
  const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);
  const currentOffers = filteredOffers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // الإحصائيات
  const stats = {
    total: offers.length,
    active: offers.filter(o => o.status === 'active').length,
    processing: offers.filter(o => o.status === 'processing').length,
    draft: offers.filter(o => o.status === 'draft').length,
    totalProducts: offers.reduce((sum, o) => sum + o.products_count, 0),
    totalViews: offers.reduce((sum, o) => sum + o.views_count, 0)
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'processing':
        return 'badge-warning';
      case 'draft':
        return 'badge-primary';
      case 'expired':
        return 'badge-error';
      default:
        return 'badge-primary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'نشط';
      case 'processing':
        return 'قيد المعالجة';
      case 'draft':
        return 'مسودة';
      case 'expired':
        return 'منتهي';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">جاري تحميل العروض...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={handleRefresh} className="btn-primary">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-responsive-xl font-bold gradient-text">
            إدارة العروض التجارية
          </h1>
          <p className="text-gray-600 mt-2">
            مراجعة وإدارة جميع العروض والفلايرز التجارية
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} className="btn-outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            تحديث
          </button>
          <Link to="/offers/new" className="btn-primary group">
            <Plus className="w-4 h-4 ml-2 group-hover:rotate-90 transition-transform duration-300" />
            إنشاء عرض جديد
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="metrics-grid">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="stat-card-modern group"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="icon-primary">
                <FileText className="w-6 h-6" />
              </div>
              <div className="text-sm font-medium text-primary-600">
                المجموع
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                {stats.total}
              </h3>
              <p className="text-sm font-medium text-gray-900 mb-1">إجمالي العروض</p>
              <p className="text-xs text-gray-600">جميع العروض المسجلة</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="stat-card-modern group"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="icon-success">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="text-sm font-medium text-success-600">
                نشط
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                {stats.active}
              </h3>
              <p className="text-sm font-medium text-gray-900 mb-1">العروض النشطة</p>
              <p className="text-xs text-gray-600">منشورة ومتاحة للجمهور</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="stat-card-modern group"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="icon-secondary">
                <Package className="w-6 h-6" />
              </div>
              <div className="text-sm font-medium text-secondary-600">
                المنتجات
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                {stats.totalProducts}
              </h3>
              <p className="text-sm font-medium text-gray-900 mb-1">إجمالي المنتجات</p>
              <p className="text-xs text-gray-600">في جميع العروض</p>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="stat-card-modern group"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="icon-warning">
                <Clock className="w-6 h-6" />
              </div>
              <div className="text-sm font-medium text-warning-600">
                معالجة
              </div>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                {stats.processing}
              </h3>
              <p className="text-sm font-medium text-gray-900 mb-1">قيد المعالجة</p>
              <p className="text-xs text-gray-600">تحت التحليل والاستخراج</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-gradient"
      >
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث في العروض..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pr-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="processing">قيد المعالجة</option>
              <option value="draft">مسودة</option>
              <option value="expired">منتهي</option>
            </select>
            
            <select
              value={merchantFilter}
              onChange={(e) => setMerchantFilter(e.target.value)}
              className="input"
            >
              <option value="all">جميع المتاجر</option>
              {merchants.map(merchant => (
                <option key={merchant.id} value={merchant.id}>
                  {merchant.name_ar || merchant.name}
                </option>
              ))}
            </select>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {filteredOffers.length} من {offers.length} عرض
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Offers Grid */}
      {currentOffers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentOffers.map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card-gradient group hover-lift"
            >
              <div className="card-header">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors duration-200">
                      {offer.title}
                    </h3>
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <Store className="w-4 h-4" />
                      {offer.merchant_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${getStatusBadge(offer.status)}`}>
                      {getStatusText(offer.status)}
                    </span>
                    <button className="text-gray-400 hover:text-gray-600 transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {offer.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {offer.description}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-secondary-600 mb-1">
                      <Package className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{offer.products_count}</div>
                    <div className="text-xs text-gray-600">منتج</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <div className="flex items-center justify-center gap-1 text-indigo-600 mb-1">
                      <Eye className="w-4 h-4" />
                    </div>
                    <div className="text-lg font-bold text-gray-900">{offer.views_count}</div>
                    <div className="text-xs text-gray-600">مشاهدة</div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {offer.start_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>بداية: {formatDate(offer.start_date)}</span>
                    </div>
                  )}
                  
                  {offer.end_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>نهاية: {formatDate(offer.end_date)}</span>
                    </div>
                  )}
                  
                  {offer.city_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Users className="w-4 h-4" />
                      <span>{offer.city_name}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
                  <div className="flex items-center gap-2">
                    <Link 
                      to={`/offers/${offer.id}`}
                      className="btn btn-sm btn-outline hover:bg-primary-50 hover:text-primary-700"
                    >
                      <Eye className="w-4 h-4 ml-1" />
                      عرض
                    </Link>
                    
                    <button 
                      onClick={() => setEditingOffer(offer)}
                      className="btn btn-sm btn-outline hover:bg-secondary-50 hover:text-secondary-700"
                    >
                      <Edit className="w-4 h-4 ml-1" />
                      تعديل
                    </button>
                    
                    <button 
                      onClick={() => setDeletingOfferId(offer.id)}
                      className="btn btn-sm btn-outline hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 ml-1" />
                      حذف
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    {formatDate(offer.created_at)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">لا توجد عروض</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all' || merchantFilter !== 'all'
              ? 'لا توجد عروض تطابق معايير البحث'
              : 'لم يتم إنشاء أي عروض بعد'
            }
          </p>
          {(!searchTerm && statusFilter === 'all' && merchantFilter === 'all') && (
            <Link to="/offers/new" className="btn-primary">
              <Plus className="w-4 h-4 ml-2" />
              إنشاء أول عرض
            </Link>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center gap-2"
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-outline"
          >
            السابق
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  currentPage === page
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-primary-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-outline"
          >
            التالي
          </button>
        </motion.div>
      )}

      {/* Edit Offer Modal */}
      {editingOffer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">تعديل العرض</h3>
              <button
                onClick={() => setEditingOffer(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const updatedData: any = {};
                
                // Debug form data
                console.log('Form data entries:');
                for (let [key, value] of formData.entries()) {
                  console.log(`${key}: ${value}`);
                }
                
                // Include fields that are accepted by the backend OfferUpdate schema
                const title = formData.get('title') as string;
                const description = formData.get('description') as string;
                const merchantId = formData.get('merchant_id') as string;
                const cityId = formData.get('city_id') as string;
                const startDate = formData.get('start_date') as string;
                const endDate = formData.get('end_date') as string;
                
                console.log('Extracted values:', {
                  title, description, merchantId, cityId, startDate, endDate
                });
                
                if (title && title.trim()) {
                  updatedData.title = title.trim();
                }
                
                if (description && description.trim()) {
                  updatedData.description = description.trim();
                }
                
                if (merchantId) {
                  updatedData.merchant_id = parseInt(merchantId);
                }
                
                if (cityId) {
                  updatedData.city_id = parseInt(cityId);
                }
                
                if (startDate) {
                  // Convert date to datetime format expected by backend
                  updatedData.start_date = `${startDate}T00:00:00`;
                }
                
                if (endDate) {
                  // Convert date to datetime format expected by backend
                  updatedData.end_date = `${endDate}T00:00:00`;
                }
                handleEditOffer(updatedData);
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان العرض
                </label>
                <input
                  type="text"
                  name="title"
                  defaultValue={editingOffer.title}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الوصف
                </label>
                <textarea
                  name="description"
                  defaultValue={editingOffer.description}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المتجر
                </label>
                <select
                  name="merchant_id"
                  defaultValue={editingOffer.merchant_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  {merchants.map((merchant: any) => (
                    <option key={merchant.id} value={merchant.id}>
                      {merchant.name_ar || merchant.name_arabic || merchant.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  المدينة
                </label>
                <select
                  name="city_id"
                  defaultValue={editingOffer.city_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  {cities.map((city: any) => (
                    <option key={city.id} value={city.id}>
                      {city.name_ar || city.name_arabic || city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ البداية
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    defaultValue={editingOffer.start_date?.split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    تاريخ الانتهاء
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    defaultValue={editingOffer.end_date?.split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  حفظ التغييرات
                </button>
                <button
                  type="button"
                  onClick={() => setEditingOffer(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingOfferId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                حذف العرض
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                هل أنت متأكد من حذف هذا العرض؟ سيتم حذف جميع المنتجات المرتبطة به أيضاً. لا يمكن التراجع عن هذا الإجراء.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteOffer(deletingOfferId)}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  حذف
                </button>
                <button
                  onClick={() => setDeletingOfferId(null)}
                  className="flex-1 btn-secondary"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default OffersManagement;